import { Button } from "@/components/ui/button"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  ArrowLeft,
  Eye,
  FileText,
  Link2,
  Mail,
  Paperclip,
  Sparkles,
} from "lucide-react"
import { useState } from "react"
import {
  INQUIRY_STATUS_DISPLAY,
  inquiryStatusLabel,
  useConnectInquiryMatch,
  useInquiryFullDetail,
  useResolveInquiryMatch,
  type InquiryMatchCandidate,
  type InquiryMatchReason,
  type InquiryMatchSuggestion,
  type InquiryStatus,
  type SupplierInquiryListItem,
} from "../../../api/supplierInquiriesApi"
import { formatDate } from "../../../utils/date"
import { DetailPanelHeader } from "../../shared/DetailPanelHeader"
import { EntityEmptyDetail } from "../../shared/EntityEmptyDetail"
import { EntityListSkeleton } from "../../shared/EntityListSkeleton"
import { McPill } from "../../shared/McPill"
import { SectionLabel } from "../../shared/SectionLabel"
import { InquiryPicker } from "./InquiryPicker"
import { InquiryRecipientsSection } from "./InquiryRecipientsSection"
import { InquiryTimelineSection } from "./InquiryTimelineSection"

const REASON_KEYS: Record<InquiryMatchReason["code"], TranslationKeys> = {
  reference_token: "MC_INQ_MATCH_REASON_REFERENCE",
  supplier_domain: "MC_INQ_MATCH_REASON_DOMAIN",
  supplier_linked: "MC_INQ_MATCH_REASON_SUPPLIER",
  only_open_for_supplier: "MC_INQ_MATCH_REASON_ONLY_OPEN",
}

/** An inquiry offered as a match — suggested by the backend, or picked by search. */
interface InquiryChoice {
  inquiryId: string
  reference: string | null
  supplierName: string | null
  status: InquiryStatus
  dueAt: string | null
  reasons?: InquiryMatchReason[]
}

function toChoice(inquiry: SupplierInquiryListItem): InquiryChoice {
  return {
    inquiryId: inquiry.id,
    reference: inquiry.reference,
    supplierName: inquiry.supplierName,
    status: inquiry.status,
    dueAt: inquiry.dueAt,
  }
}

function suggestionToChoice(suggestion: InquiryMatchSuggestion): InquiryChoice {
  return { ...suggestion, reasons: suggestion.reasons }
}

interface MatchCandidatePanelProps {
  candidate: InquiryMatchCandidate | undefined
}

/**
 * Review surface for one unmatched email: the message itself on the right of the list,
 * so the reviewer decides from the actual content rather than from a row summary.
 *
 * Two pages. The review page holds the email and the candidate inquiries; opening one
 * swaps in a read-only view of that inquiry so the reviewer can confirm it is the right
 * one before connecting, then comes back. Connecting is always a separate, explicit
 * click — picking an inquiry from the search only stages it.
 */
export function MatchCandidatePanel({ candidate }: MatchCandidatePanelProps) {
  const { translate } = useTranslation()
  const connect = useConnectInquiryMatch()
  const resolve = useResolveInquiryMatch()
  // Choosing from the search stages the inquiry rather than connecting it — selecting a
  // row in a dropdown reads as "picked", not "committed", and this is a one-way action.
  const [picked, setPicked] = useState<InquiryChoice | null>(null)
  const [viewing, setViewing] = useState<InquiryChoice | null>(null)

  if (viewing) {
    return (
      <MatchInquiryPreview
        choice={viewing}
        disabled={connect.isPending || resolve.isPending}
        onBack={() => setViewing(null)}
        onConnect={() => {
          if (candidate) {
            connect.mutate({
              candidateId: candidate.candidateId,
              inquiryId: viewing.inquiryId,
            })
          }
          setViewing(null)
        }}
      />
    )
  }

  if (!candidate) return <EntityEmptyDetail body="MC_MD_EMPTY_SELECT_BODY_REPLY" />

  const busy = connect.isPending || resolve.isPending

  function handleConnect(inquiryId: string) {
    if (!candidate) return
    connect.mutate({ candidateId: candidate.candidateId, inquiryId })
  }

  function handleResolve(resolution: "standalone" | "dismissed") {
    if (!candidate) return
    resolve.mutate({ candidateId: candidate.candidateId, resolution })
  }

  return (
    // absolute inset-0, as the inquiry detail pane does: sizes against the ScrollArea's
    // positioned root rather than its content-sized wrapper, so the header has a real
    // width to measure against and the body scrolls itself.
    <div className="absolute inset-0 flex flex-col">
      <DetailPanelHeader
        label={translate("MC_INQ_MATCH_REVIEW")}
        title={
          candidate.subject ??
          candidate.fileName ??
          translate("MC_INQ_MATCH_NO_SUBJECT")
        }
        subtitle={[
          candidate.sender ?? translate("MC_INQ_MATCH_UNKNOWN_SENDER"),
          formatDate(candidate.receivedAt),
        ]
          .filter(Boolean)
          .join(" · ")}
      />

      <div className="flex-1 space-y-6 overflow-y-auto px-4 py-4">
        <section>
          <SectionLabel>{translate("MC_INQ_MATCH_EMAIL")}</SectionLabel>
          <div className="mt-2 rounded-lg border border-grey4">
            <dl className="space-y-1 border-b border-grey4 px-3 py-2 text-xs">
              <MetaRow
                label={translate("MC_INQ_MATCH_FROM")}
                value={candidate.sender}
              />
              <MetaRow
                label={translate("MC_INQ_MATCH_TO")}
                value={candidate.recipientsTo.join(", ") || null}
              />
            </dl>
            {candidate.body ? (
              <p className="whitespace-pre-wrap wrap-break-word px-3 py-3 text-sm text-white">
                {candidate.body}
              </p>
            ) : (
              <p className="px-3 py-3 text-sm italic text-grey5">
                {translate("MC_INQ_MATCH_NO_BODY")}
              </p>
            )}
          </div>
        </section>

        {candidate.attachments.length > 0 && (
          <section>
            <SectionLabel>
              {translate("MC_INQ_MATCH_ATTACHMENTS", {
                n: candidate.attachments.length,
              })}
            </SectionLabel>
            <ul className="mt-2 space-y-1.5">
              {candidate.attachments.map((attachment, index) => (
                <li
                  key={`${attachment.filename ?? "file"}-${index}`}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-grey4 px-3 py-2"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Paperclip size={13} className="shrink-0 text-grey5" />
                    <span className="truncate text-sm text-white">
                      {attachment.filename ?? "—"}
                    </span>
                    {attachment.declarationNumber && (
                      <McPill tone="info">
                        {attachment.declarationNumber}
                      </McPill>
                    )}
                  </div>
                  {attachment.fileUrl && (
                    <a
                      href={attachment.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-xs text-yellow1 hover:underline"
                    >
                      {translate("MC_INQ_MATCH_OPEN_FILE")}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <SectionLabel>{translate("MC_INQ_MATCH_CONNECT_TO")}</SectionLabel>
          {candidate.suggestions.length > 0 ? (
            <ul className="mt-2 space-y-2">
              {candidate.suggestions.map((suggestion) => (
                <InquiryChoiceCard
                  key={suggestion.inquiryId}
                  choice={suggestionToChoice(suggestion)}
                  disabled={busy}
                  onConnect={() => handleConnect(suggestion.inquiryId)}
                  onView={() => setViewing(suggestionToChoice(suggestion))}
                />
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-grey5">
              {translate("MC_INQ_MATCH_NO_SUGGESTIONS")}
            </p>
          )}
          <div className="mt-3">
            <InquiryPicker
              selectedLabel={
                picked
                  ? (picked.supplierName ?? picked.reference ?? undefined)
                  : undefined
              }
              onSelect={(inquiry) => setPicked(toChoice(inquiry))}
              disabled={busy}
            />
          </div>
          {picked && (
            <ul className="mt-2">
              <InquiryChoiceCard
                choice={picked}
                disabled={busy}
                onConnect={() => handleConnect(picked.inquiryId)}
                onView={() => setViewing(picked)}
              />
            </ul>
          )}
        </section>

        <section>
          <SectionLabel>{translate("MC_INQ_MATCH_NOT_A_REPLY")}</SectionLabel>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => handleResolve("standalone")}
            >
              <FileText size={13} />
              {translate("MC_INQ_MATCH_STANDALONE")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => handleResolve("dismissed")}
            >
              {translate("MC_INQ_MATCH_DISMISS")}
            </Button>
          </div>
        </section>
      </div>
    </div>
  )
}

/**
 * Page two: the chosen inquiry, read-only.
 *
 * Deliberately carries none of the inquiry's own actions (remind, pause reminders, close,
 * discard) — this is here to answer "is this the right inquiry?", and those actions belong
 * on the inquiry's own tab where their context is.
 */
function MatchInquiryPreview({
  choice,
  disabled,
  onBack,
  onConnect,
}: {
  choice: InquiryChoice
  disabled: boolean
  onBack: () => void
  onConnect: () => void
}) {
  const { translate } = useTranslation()
  const { data: inquiry, isLoading } = useInquiryFullDetail(choice.inquiryId)
  const display = inquiry ? INQUIRY_STATUS_DISPLAY[inquiry.status] : undefined

  return (
    <div className="absolute inset-0 flex flex-col">
      <DetailPanelHeader
        label={translate("MC_INQ_MATCH_PREVIEW_LABEL")}
        title={inquiry?.supplierName ?? choice.supplierName ?? "—"}
        subtitle={inquiry?.reference ?? choice.reference ?? "—"}
        actions={({ iconOnly }) => (
          <>
            {display && (
              <McPill tone={display.tone}>{translate(display.labelKey)}</McPill>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-[30px] gap-1.5 text-[12.5px]"
              aria-label={translate("MC_INQ_MATCH_BACK")}
              title={translate("MC_INQ_MATCH_BACK")}
              onClick={onBack}
            >
              <ArrowLeft size={13} />
              {!iconOnly && translate("MC_INQ_MATCH_BACK")}
            </Button>
            <Button
              size="sm"
              disabled={disabled}
              className="h-[30px] gap-1.5 bg-yellow1 text-[12.5px] text-black hover:bg-yellow2"
              aria-label={translate("MC_INQ_MATCH_CONNECT")}
              title={translate("MC_INQ_MATCH_CONNECT")}
              onClick={onConnect}
            >
              <Link2 size={13} />
              {!iconOnly && translate("MC_INQ_MATCH_CONNECT")}
            </Button>
          </>
        )}
      />
      <div className="flex-1 overflow-y-auto">
        {isLoading || !inquiry ? (
          <div className="p-4">
            <EntityListSkeleton />
          </div>
        ) : (
          <>
            <InquiryRecipientsSection
              recipientsTo={inquiry.recipientsTo}
              recipientsCc={inquiry.recipientsCc}
            />
            <InquiryTimelineSection inquiry={inquiry} />
          </>
        )}
      </div>
    </div>
  )
}

function MetaRow({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <div className="flex flex-wrap gap-2">
      <dt className="shrink-0 text-grey5">{label}</dt>
      <dd className="min-w-0 flex-1 wrap-break-word text-white">{value}</dd>
    </div>
  )
}

function InquiryChoiceCard({
  choice,
  disabled,
  onConnect,
  onView,
}: {
  choice: InquiryChoice
  disabled: boolean
  onConnect: () => void
  onView: () => void
}) {
  const { translate } = useTranslation()
  return (
    <li className="rounded-lg border border-grey4 px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm text-white">
            {choice.supplierName ?? "—"}
          </div>
          <div className="mt-0.5 truncate text-xs text-grey5">
            {[
              choice.reference,
              inquiryStatusLabel(choice.status, translate),
              choice.dueAt
                ? translate("MC_INQ_MATCH_DUE", {
                    date: formatDate(choice.dueAt),
                  })
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="outline" onClick={onView}>
            <Eye size={13} />
            {translate("MC_INQ_MATCH_VIEW")}
          </Button>
          <Button size="sm" disabled={disabled} onClick={onConnect}>
            <Link2 size={13} />
            {translate("MC_INQ_MATCH_CONNECT")}
          </Button>
        </div>
      </div>
      {choice.reasons && choice.reasons.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {choice.reasons.map((reason, index) => (
            <li
              key={`${reason.code}-${index}`}
              className="flex items-center gap-1 rounded-md bg-grey2 px-1.5 py-0.5 text-[11px] text-grey5"
            >
              <Sparkles size={10} className="shrink-0 text-yellow1" />
              {translate(REASON_KEYS[reason.code], {
                detail: reason.detail ?? "",
              })}
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/** Left-list row for one candidate — summary only; the decision happens in the panel. */
export function MatchCandidateRow({
  candidate,
}: {
  candidate: InquiryMatchCandidate
}) {
  const { translate } = useTranslation()
  const fileCount = candidate.attachments.length
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <Mail size={13} className="shrink-0 text-grey5" />
        <span className="truncate text-sm text-white">
          {candidate.sender ?? translate("MC_INQ_MATCH_UNKNOWN_SENDER")}
        </span>
      </div>
      <div className="mt-1 truncate text-xs text-white">
        {candidate.subject ??
          candidate.fileName ??
          translate("MC_INQ_MATCH_NO_SUBJECT")}
      </div>
      <div className="mt-1 flex items-center gap-2 text-xs text-grey5">
        <span>{formatDate(candidate.receivedAt)}</span>
        {fileCount > 0 && (
          <span className="flex items-center gap-1">
            <Paperclip size={11} />
            {fileCount}
          </span>
        )}
        {candidate.suggestions.length > 0 && (
          <McPill tone="info">
            {translate("MC_INQ_MATCH_SUGGESTED", {
              n: candidate.suggestions.length,
            })}
          </McPill>
        )}
      </div>
    </div>
  )
}
